<script setup lang="ts">
import type { MegamenuNavItem } from '~/data/megamenu'
import { useIsSubdomain } from '~/composables/useSubdomain'

export interface INavLinkItem {
  path: string
  text: string
  /* whether to show this nav item in  */
  showOnSub?: boolean
}

defineEmits(['navbarClose'])

defineProps<{
  isOpen: boolean
  navLinks: INavLinkItem[]
  megamenuData?: MegamenuNavItem[]
}>()

const isSubdomain = useIsSubdomain()
const { t } = useI18n()
const isMobileSubviewOpen = ref(false)
</script>
<template>
  <nav
    id="navbar-sticky"
    class="navbar-sticky"
    :class="[isOpen ? 'navbar-sticky--open' : '']"
  >
    <div
      class="max-w-screen-xl md:px-4 py-4 md:py-1 md:mx-auto h-full flex flex-col"
    >
      <div
        v-show="!isMobileSubviewOpen"
        class="border-b border-gray-200 md:hidden"
      >
        <IconClose
          class="h-5 w-5 mt-2 ml-5 mb-4 md:hidden text-teal-900 cursor-pointer"
          aria-label="Close menu"
          @click="$emit('navbarClose')"
        />
      </div>
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between flex-1 min-h-0"
      >
        <!-- Desktop: megamenu with dropdown -->
        <MegamenuDesktop
          v-if="megamenuData"
          v-show="navLinks.length && megamenuData?.length"
          class="hidden md:block"
          :nav-links="navLinks"
          :megamenu-data="megamenuData"
          @navbar-close="$emit('navbarClose')"
        /><!-- Desktop fallback when no megamenu data -->
        <ul
          v-else
          class="hidden md:flex flex-col md:flex-row font-medium mt-0 space-y-4 md:space-y-0 md:space-x-8 rtl:space-x-reverse text-sm"
        >
          <li
            v-for="item in navLinks"
            :key="item.path"
            class="border-b pb-4 md:border-none md:pb-0"
          >
            <NuxtLinkLocale
              class="text-stone-800 text-sm font-normal navigation-link"
              :to="item.path"
              @click="$emit('navbarClose')"
            >
              {{ item.text }}
            </NuxtLinkLocale>
          </li>
        </ul>
        <!-- Mobile: megamenu list + panels -->
        <MegamenuMobile
          v-if="navLinks.length && megamenuData?.length"
          class="md:hidden flex-1 min-h-0 flex flex-col"
          :nav-links="navLinks"
          :megamenu-data="megamenuData"
          @navbar-close="$emit('navbarClose')"
          @subview-open="isMobileSubviewOpen = $event"
        />
        <!-- Mobile fallback when no megamenu data -->
        <ul
          v-else
          class="md:hidden flex flex-col font-medium mt-0 space-y-4 text-sm"
        >
          <li v-for="item in navLinks" :key="item.path" class="border-b pb-4">
            <NuxtLinkLocale
              class="text-stone-800 text-sm font-normal"
              :to="item.path"
              @click="$emit('navbarClose')"
            >
              {{ item.text }}
            </NuxtLinkLocale>
          </li>
        </ul>
        <TeamEventRequestButton v-if="!isSubdomain" class="hidden sm:block">
          {{ t('home.headerButtonTitle') }}
        </TeamEventRequestButton>
      </div>
      <div v-show="!isMobileSubviewOpen" class="ml-4">
        <LocaleSwitcher @navbar-close="$emit('navbarClose')" />
        <MinimalContactInfo />
      </div>
    </div>
  </nav>
</template>

<style lang="postcss" scoped>
.navbar-sticky {
  @apply fixed left-0 top-0 md:left-auto md:relative w-full h-[100dvh] md:h-fit bg-white hidden md:block border-b border-gray-200;

  &--open {
    @apply block;
  }
}

.router-link-active {
  @apply font-semibold underline;
}

.navigation-link {
  @apply relative inline-block no-underline;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: currentColor;
    transform: scaleX(0);
    transform-origin: var(--hover-origin, center);
    transition:
      transform 0.3s ease,
      transform-origin 0.1s ease;
  }

  &.router-link-active,
  &.router-link-exact-active,
  &:hover {
    &::after {
      transform: scaleX(1);
    }
  }
}
</style>
